import { API } from "@swivl-mono/api-helper";
import { filterClasses } from "@swivl-mono/helpers";
import { LocationOption, LocationPickerData, StructuredActionTaken } from "@swivl-mono/types";
import React, { useEffect, useRef, useState } from "react";
import { MdLocationOn } from "react-icons/md";

import { formatTime } from "../../Helpers/formatTime";
import type { ChatMessage } from "../../Types/BotChat.Types";

interface LocationPickerProps {
  data: LocationPickerData;
  message: ChatMessage;
  /** Called when the user taps a location card. Stage 7 wires this to the socket. */
  onAction?: (action: StructuredActionTaken) => void;
  /** Read-only mode for the agent timeline. No tap handlers; renders selected state if actionTaken. */
  readOnly?: boolean;
  /** Called when the user requests a wider search (only when canExpandRadius). */
  onExpandRadius?: () => void;
  /**
   * Push a new `payload.data` after picker-API filter refresh / zip-entry submit /
   * "Use my location". See widget design doc §A4.6.
   */
  onUpdateData?: (data: Record<string, unknown>) => void;
  /** Org context — required for picker-API calls. */
  orgId?: number;
  /** Conversation id for picker-API debug logging. */
  conversationId?: number | null;
  /**
   * Optional Google Places rating resolver. When provided, each card
   * fetches its facility's rating on mount + renders a star + review
   * count row beneath the address. Null result hides the row gracefully
   * (no placeId, no rating yet, etc).
   */
  fetchPlacesRating?: (locationId: number) => Promise<{ rating: number; userRatingsCount: number } | null>;
}

/**
 * `location-picker` structured-message renderer. Has three render modes:
 *   - **Selected** — `actionTaken` is present; shows the picked location with a checkmark.
 *   - **Zip-entry** — `data.mode === "zip-entry"`; renders an inline zip input
 *     (per Figma node 275-5687). On submit, calls the embed-picker API
 *     directly and merges results into this same message via `onUpdateData`.
 *   - **Results** (default) — renders tappable location cards.
 *
 * See:
 *   docs/snowy/plans/2026-04-28-structured-message-protocol.md §5.2
 *   /Users/chuck/.claude/plans/ancient-percolating-lantern.md §A4.3 (zip-entry mode)
 */
export function LocationPicker({
  data,
  message,
  onAction,
  readOnly = false,
  onExpandRadius,
  onUpdateData,
  orgId,
  conversationId,
  fetchPlacesRating
}: LocationPickerProps): React.ReactElement {
  // Selected state — message has actionTaken.
  if (message.actionTaken) {
    const action = message.actionTaken as StructuredActionTaken;
    if (action.type === "selected" && typeof action.target === "string") {
      const snapshot = action.snapshot as LocationOption | undefined;
      const picked = snapshot ?? data.locations.find((l) => l.id === action.target);
      if (picked) {
        return <SelectedLocationCard location={picked} readOnly={readOnly} />;
      }
    }
    if (action.type === "dismissed") {
      return <DismissedNote time={action.takenAt ? formatTime(action.takenAt) : ""} readOnly={readOnly} label="No location selected" />;
    }
  }

  // Zip-entry mode — inline zip-input form. Submit calls the embed-picker
  // API and pushes the new data via `onUpdateData` (which uses the
  // picker-data-updated socket event server-side).
  if (data.mode === "zip-entry" && !readOnly) {
    return <ZipEntryForm message={message} data={data} onUpdateData={onUpdateData} orgId={orgId} conversationId={conversationId} />;
  }

  // Empty results — distinct from zip-entry mode (which is intentionally empty).
  // Used when find_locations returned no matches near the user's zip, OR when
  // a filter refresh (different zip / wider radius / use-my-location) came up
  // empty. We always render the filter card above the message so the customer
  // can correct course (try a different zip, widen the radius, etc.) — without
  // it the picker would dead-end. The "Search wider" fallback below only fires
  // when the parent didn't wire `onUpdateData`/`orgId` (e.g. older host code).
  if (data.locations.length === 0) {
    return (
      <div className="flex flex-col gap-2">
        {!readOnly && onUpdateData && orgId !== undefined && (
          <ResultsFilterRow message={message} data={data} onUpdateData={onUpdateData} orgId={orgId} conversationId={conversationId} />
        )}
        <div className="rounded-lg border border-grey-200 bg-white p-3 text-sm text-grey-600">
          No locations found within {data.search.radiusMiles} miles of {data.search.zip || "that area"}. Try a different zip code or widen your search
          radius.
          {data.search.canExpandRadius && onExpandRadius && !onUpdateData && (
            <button type="button" onClick={onExpandRadius} className="ml-2 text-primary-500 underline">
              Search wider
            </button>
          )}
        </div>
      </div>
    );
  }

  // Interactive picker — filter card + paginated location carousel. Matches
  // Figma 370:21754: filter and the active location card are separate,
  // individually bordered cards stacked with gap-2. When there are multiple
  // results, the card shows one at a time and a ‹ • • • • › nav at the
  // bottom for stepping through.
  return (
    <div className="flex flex-col gap-2">
      {!readOnly && onUpdateData && orgId !== undefined && (
        <ResultsFilterRow message={message} data={data} onUpdateData={onUpdateData} orgId={orgId} conversationId={conversationId} />
      )}
      <LocationCarousel
        locations={data.locations}
        readOnly={readOnly}
        onTap={readOnly ? undefined : (loc) => onAction?.(buildSelectedAction(loc))}
        fetchPlacesRating={fetchPlacesRating}
        // Shopping intent chains into unit browsing; an informational pick
        // just selects the location and the bot answers the question.
        ctaLabel={data.chainTo === "unit-picker" ? "View Units" : "Select"}
      />
      {data.search.canExpandRadius && !readOnly && onExpandRadius && (
        <button
          type="button"
          onClick={onExpandRadius}
          className="rounded-lg border border-grey-200 bg-white px-3 py-2 text-sm text-grey-600 hover:bg-grey-50"
        >
          Search wider
        </button>
      )}
    </div>
  );
}

interface LocationCarouselProps {
  locations: LocationOption[];
  readOnly: boolean;
  onTap?: (location: LocationOption) => void;
  fetchPlacesRating?: (locationId: number) => Promise<{ rating: number; userRatingsCount: number } | null>;
  /** Card CTA text — "View Units" when the pick chains into unit browsing, else "Select". */
  ctaLabel: string;
}

/**
 * Wraps a single LocationCardBody with carousel state when there are
 * multiple results. The outer bordered shell + nav (chevrons + dots) stay
 * static; on navigation a two-card horizontal track slides a full card width
 * so the outgoing card physically slides out as the incoming one slides in
 * (a filmstrip). `›` slides toward the next card (from the right), `‹` toward
 * the previous (from the left). Wraps around at both ends. Per Figma 370:21754.
 */
/** Min movement (px) before pointer activity counts as a drag rather than a tap. */
const DRAG_ARM_THRESHOLD = 8;
/** Horizontal delta (px) at release that commits a swipe to the next/prev card. */
const SWIPE_COMMIT_THRESHOLD = 50;
/** Duration (ms) of the card-to-card slide transition. */
const SLIDE_MS = 350;
/** Gap (px) between the two cards mid-slide so they never visually merge. */
const SLIDE_GAP = 16;

function LocationCarousel({ locations, readOnly, onTap, fetchPlacesRating, ctaLabel }: LocationCarouselProps): React.ReactElement | null {
  const [index, setIndex] = useState(0);
  // Live drag offset — drives an inline translateX on the body so the card
  // visually follows the pointer. Reset to 0 on release. Lives in state
  // (not a ref) so the body re-renders on each pointermove. The renders are
  // cheap because LocationCardBody's content is static during drag.
  const [dragOffset, setDragOffset] = useState(0);
  // True for the snap-back window after a sub-threshold release — drives a
  // CSS transition on `transform` so the card glides back to 0 instead of
  // snapping. Cleared on `transitionend`.
  const [isSnappingBack, setIsSnappingBack] = useState(false);
  // Active card-to-card slide (chevron/dot nav + committed swipe). Renders a
  // two-card horizontal track that translates a full card width so the
  // outgoing card slides out as the incoming one slides in — a filmstrip, not
  // a fade-in. `settled` flips true one frame after mount so the browser
  // transitions from the start position to the end. `startPx` seeds the start
  // from a committed swipe's release offset so the glide continues from the
  // finger instead of jumping. null when idle.
  const [slide, setSlide] = useState<{ from: number; to: number; dir: "left" | "right"; startPx: number; settled: boolean } | null>(null);
  // Pointer tracking — `wasDrag` stays true through the synthetic click that
  // follows pointerup so we can suppress it (otherwise buttons under the
  // pointer would fire after a drag).
  const dragStartXRef = useRef<number | null>(null);
  const wasDragRef = useRef(false);
  // Respect the OS "reduce motion" setting — jump between cards instead of sliding.
  const prefersReducedMotion =
    typeof window !== "undefined" && typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  // One frame after a slide mounts at its start position, flip `settled` to
  // kick off the CSS transition toward the end position.
  useEffect(() => {
    if (!slide || slide.settled) return;
    const raf = requestAnimationFrame(() => setSlide((s) => (s && !s.settled ? { ...s, settled: true } : s)));
    return () => cancelAnimationFrame(raf);
  }, [slide]);

  if (locations.length === 0) return null;
  // Clamp in case server-broadcast updates shrunk the list out from under us.
  const safeIndex = Math.min(index, locations.length - 1);
  const current = locations[safeIndex];

  // Start a slide toward `next`. Used for chevrons (wrap-around), dot taps
  // (direction follows index delta), and committed swipes. Ignored mid-slide
  // so rapid clicks don't stack. Reduced-motion jumps without animating.
  const goTo = (next: number, dir: "left" | "right"): void => {
    if (next === safeIndex || slide) return;
    if (prefersReducedMotion) {
      setIndex(next);
      return;
    }
    setSlide({ from: safeIndex, to: next, dir, startPx: 0, settled: false });
  };

  const goPrev = (): void => goTo((safeIndex - 1 + locations.length) % locations.length, "left");
  const goNext = (): void => goTo((safeIndex + 1) % locations.length, "right");
  const goToDot = (i: number): void => goTo(i, i > safeIndex ? "right" : "left");

  // ─── Pointer (touch + mouse + pen) swipe handling ────────────────────────
  // Single set of handlers thanks to the Pointer Events API. `setPointerCapture`
  // keeps tracking even when the cursor leaves the carousel mid-drag.
  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>): void => {
    if (locations.length <= 1 || slide) return;
    if (e.pointerType === "mouse" && e.button !== 0) return;
    dragStartXRef.current = e.clientX;
    wasDragRef.current = false;
    // Cancel an in-flight snap-back so a quick second drag picks up cleanly.
    if (isSnappingBack) setIsSnappingBack(false);
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>): void => {
    if (dragStartXRef.current === null) return;
    const delta = e.clientX - dragStartXRef.current;
    if (!wasDragRef.current && Math.abs(delta) > DRAG_ARM_THRESHOLD) {
      wasDragRef.current = true;
      e.currentTarget.setPointerCapture(e.pointerId);
    }
    if (wasDragRef.current) setDragOffset(delta);
  };

  const handlePointerUp = (e: React.PointerEvent<HTMLDivElement>): void => {
    if (dragStartXRef.current === null) return;
    const delta = e.clientX - dragStartXRef.current;
    const dragged = wasDragRef.current;
    dragStartXRef.current = null;
    if (dragged && e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }

    if (Math.abs(delta) < SWIPE_COMMIT_THRESHOLD) {
      // Short drag → treat as tap; let the click through normally. If we
      // actually moved the card, glide it back via CSS transition.
      wasDragRef.current = false;
      if (dragged) {
        setIsSnappingBack(true);
        setDragOffset(0);
      }
      return;
    }
    // Commit: hand off to a two-card slide that continues from the release
    // offset (startPx = delta) so the motion is seamless. wasDragRef stays
    // true so the upcoming synthetic click gets swallowed below.
    setIsSnappingBack(false);
    setDragOffset(0);
    const dir = delta < 0 ? "right" : "left";
    const to = delta < 0 ? (safeIndex + 1) % locations.length : (safeIndex - 1 + locations.length) % locations.length;
    if (prefersReducedMotion) setIndex(to);
    else setSlide({ from: safeIndex, to, dir, startPx: delta, settled: false });
  };

  const handlePointerCancel = (): void => {
    dragStartXRef.current = null;
    wasDragRef.current = false;
    if (dragOffset !== 0) {
      setIsSnappingBack(true);
      setDragOffset(0);
    }
  };

  // Capture-phase click handler swallows the synthetic click that fires
  // after a committed swipe. Without this, a swipe that ends over the "View
  // Units" button (or any nav button) would also click it.
  const handleClickCapture = (e: React.MouseEvent<HTMLDivElement>): void => {
    if (!wasDragRef.current) return;
    e.preventDefault();
    e.stopPropagation();
    wasDragRef.current = false;
  };

  const handleTransitionEnd = (e: React.TransitionEvent<HTMLDivElement>): void => {
    if (e.propertyName === "transform" && isSnappingBack) setIsSnappingBack(false);
  };

  // Body style (single card, idle/drag): during drag the card translates 1:1
  // with the pointer; during snap-back the transition glides it back to 0.
  const bodyStyle: React.CSSProperties = {};
  if (dragOffset !== 0) bodyStyle.transform = `translateX(${dragOffset}px)`;
  if (isSnappingBack) bodyStyle.transition = "transform 220ms ease-out";

  // Each cell mirrors the idle single-card wrapper (`flex flex-col gap-2`) so a
  // card is the exact same height whether it's sliding or at rest — otherwise
  // the collapsed gaps would shrink the card mid-slide.
  const slideCard = (loc: LocationOption, cellKey: string): React.ReactElement => (
    <div key={cellKey} className="flex w-full shrink-0 flex-col gap-2">
      <LocationCardBody
        location={loc}
        readOnly={readOnly}
        onTap={onTap ? () => onTap(loc) : undefined}
        fetchPlacesRating={fetchPlacesRating}
        ctaLabel={ctaLabel}
      />
    </div>
  );

  // Finalize the slide: adopt the destination index and drop the track. The
  // card shown at the slide's end is the same one that becomes centered, so
  // the swap is invisible. Guard on currentTarget so a child's transition
  // (e.g. a button hover) doesn't finalize early.
  const handleSlideEnd = (e: React.TransitionEvent<HTMLDivElement>): void => {
    if (e.target !== e.currentTarget || e.propertyName !== "transform" || !slide || !slide.settled) return;
    setIndex(slide.to);
    setSlide(null);
  };

  // Track box width == container width, so translateX ±100% swaps a full card.
  // Next: [from, to] slides 0 → -100%. Prev: [to, from] slides -100% → 0.
  let trackStyle: React.CSSProperties | undefined;
  if (slide) {
    // Full swap distance = one card width (100%) plus the inter-card gap, so
    // the two cards never touch (their identical pink "View Units" buttons
    // would otherwise appear as one merged button while sliding).
    const startPos = slide.dir === "right" ? "0px" : `-100% - ${SLIDE_GAP}px`;
    const endPos = slide.dir === "right" ? `-100% - ${SLIDE_GAP}px` : "0px";
    const at = slide.settled ? endPos : startPos;
    const px = slide.settled ? 0 : slide.startPx;
    trackStyle = {
      gap: SLIDE_GAP,
      transform: `translateX(calc(${at} + ${px}px))`,
      transition: slide.settled ? `transform ${SLIDE_MS}ms cubic-bezier(0.4, 0, 0.2, 1)` : "none"
    };
  }

  return (
    <div
      className="flex touch-pan-y flex-col gap-2 overflow-hidden rounded-lg border border-grey-200 bg-white p-3 select-none"
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerCancel}
      onClickCapture={handleClickCapture}
    >
      {slide ? (
        <div className="flex" style={trackStyle} onTransitionEnd={handleSlideEnd}>
          {slide.dir === "right"
            ? [slideCard(locations[slide.from], "from"), slideCard(locations[slide.to], "to")]
            : [slideCard(locations[slide.to], "to"), slideCard(locations[slide.from], "from")]}
        </div>
      ) : (
        <div className="flex flex-col gap-2" style={bodyStyle} onTransitionEnd={handleTransitionEnd}>
          <LocationCardBody
            location={current}
            readOnly={readOnly}
            onTap={onTap ? () => onTap(current) : undefined}
            fetchPlacesRating={fetchPlacesRating}
            ctaLabel={ctaLabel}
          />
        </div>
      )}
      {locations.length > 1 && (
        <CarouselNav index={slide ? slide.to : safeIndex} count={locations.length} onPrev={goPrev} onNext={goNext} onSelectDot={goToDot} />
      )}
    </div>
  );
}

interface CarouselNavProps {
  index: number;
  count: number;
  onPrev: () => void;
  onNext: () => void;
  onSelectDot: (i: number) => void;
}

/** Cap on visible pager dots before the window-and-shrink behavior kicks in. */
const MAX_VISIBLE_DOTS = 5;

function CarouselNav({ index, count, onPrev, onNext, onSelectDot }: CarouselNavProps): React.ReactElement {
  // Slide a fixed-size window of dots so the active dot stays near the
  // middle. Edge dots shrink when there's more content beyond them in that
  // direction, hinting at additional pages (Instagram-style). When the
  // total count fits within MAX_VISIBLE_DOTS, all dots render full-size.
  const useWindow = count > MAX_VISIBLE_DOTS;
  const windowStart = useWindow ? Math.max(0, Math.min(count - MAX_VISIBLE_DOTS, index - Math.floor(MAX_VISIBLE_DOTS / 2))) : 0;
  const windowEnd = useWindow ? windowStart + MAX_VISIBLE_DOTS : count;

  return (
    <div className="flex items-center justify-center gap-2 pt-1">
      <button type="button" onClick={onPrev} aria-label="Previous location" className="cursor-pointer p-1 text-grey-500 hover:text-grey-700">
        <ChevronLeftIcon />
      </button>
      <div className="flex items-center gap-1.5">
        {Array.from({ length: windowEnd - windowStart }, (_, offset) => {
          const i = windowStart + offset;
          const active = i === index;
          // An edge dot is "smaller" only when there are more dots beyond
          // it in that direction — otherwise it's the genuine first/last
          // dot and renders full-size.
          const isLeftEdge = i === windowStart && windowStart > 0;
          const isRightEdge = i === windowEnd - 1 && windowEnd < count;
          const isSmaller = isLeftEdge || isRightEdge;
          return (
            <button
              key={i}
              type="button"
              onClick={() => onSelectDot(i)}
              aria-label={`Go to location ${i + 1}`}
              aria-current={active ? "true" : undefined}
              className={filterClasses(
                "cursor-pointer rounded-full transition-all duration-200",
                isSmaller ? "h-1 w-1" : "h-1.5 w-1.5",
                active ? "bg-primary-500" : "bg-grey-300 hover:bg-grey-400"
              )}
            />
          );
        })}
      </div>
      <button type="button" onClick={onNext} aria-label="Next location" className="cursor-pointer p-1 text-grey-500 hover:text-grey-700">
        <ChevronRightIcon />
      </button>
    </div>
  );
}

function ChevronLeftIcon(): React.ReactElement {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polyline points="15 18 9 12 15 6" />
    </svg>
  );
}

function ChevronRightIcon(): React.ReactElement {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polyline points="9 18 15 12 9 6" />
    </svg>
  );
}

const RADIUS_OPTIONS: ReadonlyArray<number> = [5, 10, 25, 50, 100];

interface ResultsFilterRowProps {
  message: ChatMessage;
  data: LocationPickerData;
  onUpdateData: (data: Record<string, unknown>) => void;
  orgId: number;
  conversationId?: number | null;
}

/**
 * Filter row above the location results list. Per Figma node 87-2361:
 * editable zip input, "Use my location" link, and a row of radius pills
 * (5/10/25/50/100 miles, selected pill highlighted in primary).
 *
 * Any change calls the embed-picker API directly and merges the new
 * results into this same message via `onUpdateData`. Per admin-observability
 * rule, each call writes a `picker_api_call` debug-log row (server side).
 */
function ResultsFilterRow({ message, data, onUpdateData, orgId, conversationId }: ResultsFilterRowProps): React.ReactElement {
  const [zipInput, setZipInput] = useState(data.search.zip);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Keep the input in sync with server-broadcast updates (e.g. another
  // tab refreshed the data, or "Use my location" set a new zip).
  useEffect(() => {
    setZipInput(data.search.zip);
  }, [data.search.zip]);

  const refresh = async (params: {
    postalCode?: string;
    lat?: number;
    lng?: number;
    distance?: number;
    sourceAction: "filter_change" | "use_my_location";
  }): Promise<void> => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      // Strip undefined entries so the api-helper doesn't serialize them as
      // `?key=undefined` (which then fails server-side TSOA validation).
      const query: Record<string, unknown> = {
        sourceMessageId: message.messageId,
        sourceAction: params.sourceAction,
        distance: params.distance ?? data.search.radiusMiles
      };
      if (params.postalCode) query["postalCode"] = params.postalCode;
      if (typeof params.lat === "number") query["lat"] = params.lat;
      if (typeof params.lng === "number") query["lng"] = params.lng;
      if (conversationId !== null && conversationId !== undefined) query["botConversationId"] = conversationId;
      const result = await API.EmbedPicker.getLocations(orgId, query);
      const merged: Record<string, unknown> = {
        ...result,
        mode: "results",
        ...(data.chainTo ? { chainTo: data.chainTo } : {})
      };
      onUpdateData(merged);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Couldn't refresh locations";
      setError(msg);
    } finally {
      setBusy(false);
    }
  };

  const handleZipSubmit = (e: React.FormEvent): void => {
    e.preventDefault();
    const zip = zipInput.trim();
    if (!zip || zip === data.search.zip) return;
    void refresh({ postalCode: zip, sourceAction: "filter_change" });
  };

  const handleRadiusSelect = (newRadius: number): void => {
    if (newRadius === data.search.radiusMiles) return;
    if (!data.search.zip) return; // need a zip to query
    void refresh({ postalCode: data.search.zip, distance: newRadius, sourceAction: "filter_change" });
  };

  const handleUseMyLocation = (): void => {
    if (!navigator.geolocation) {
      setError("Geolocation isn't available in this browser");
      return;
    }
    setBusy(true);
    setError(null);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        void refresh({ lat: pos.coords.latitude, lng: pos.coords.longitude, sourceAction: "use_my_location" });
      },
      (err) => {
        setBusy(false);
        const msg = err.code === err.PERMISSION_DENIED ? "Location permission denied" : "Couldn't get your location";
        setError(msg);
      },
      { enableHighAccuracy: false, timeout: 10000, maximumAge: 60000 }
    );
  };

  return (
    <div className="flex flex-col gap-2 rounded-lg border border-grey-200 bg-white p-3">
      <form onSubmit={handleZipSubmit} className="flex items-center gap-2">
        <span className="text-sm text-grey-500">Near</span>
        <input
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={10}
          value={zipInput}
          onChange={(e) => setZipInput(e.target.value)}
          disabled={busy}
          className="h-10 w-20 rounded-sm border border-grey-300 bg-linear-to-b from-grey-50 via-white via-22% to-white px-3 py-2 text-base text-grey-900 focus:border-secondary focus:ring-2 focus:ring-secondary/30 focus:outline-none"
        />
        <button
          type="button"
          onClick={handleUseMyLocation}
          disabled={busy}
          className="flex cursor-pointer items-center gap-1 text-sm font-semibold text-primary-500 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <CurrentLocationIcon />
          Use my location
        </button>
      </form>
      <div className="flex items-center gap-2">
        <span className="text-sm text-grey-500">Miles</span>
        <div className="flex flex-1 items-center justify-between">
          {RADIUS_OPTIONS.map((r) => {
            const active = r === data.search.radiusMiles;
            return (
              <button
                key={r}
                type="button"
                disabled={busy}
                onClick={() => handleRadiusSelect(r)}
                className={filterClasses(
                  "flex w-12 cursor-pointer items-center justify-center rounded-lg border px-2 py-1 text-base font-semibold",
                  active ? "border-primary-500 bg-primary-500 text-white" : "border-grey-200 bg-grey-50 text-grey-900 hover:bg-grey-100",
                  busy && "cursor-not-allowed opacity-50"
                )}
              >
                {r}
              </button>
            );
          })}
        </div>
      </div>
      {busy && (
        <div className="flex items-center gap-1.5 text-xs text-grey-500">
          <Spinner />
          Searching…
        </div>
      )}
      {error && <div className="text-xs text-red-600">{error}</div>}
    </div>
  );
}

function buildSelectedAction(location: LocationOption): StructuredActionTaken {
  return {
    type: "selected",
    takenAt: new Date().toISOString(),
    target: location.id,
    snapshot: location
  };
}

interface ZipEntryFormProps {
  message: ChatMessage;
  data: LocationPickerData;
  onUpdateData?: (data: Record<string, unknown>) => void;
  orgId?: number;
  conversationId?: number | null;
}

/**
 * Zip-entry form — inline input + submit. On submit, calls the picker API
 * and replaces this message's `data` with the results (mode flips to
 * "results"). Matches Figma node 370:21627: header row with pink location
 * icon, gradient text field with embedded submit arrow, and a
 * "Use my location" geolocation shortcut.
 */
function ZipEntryForm({ message, data, onUpdateData, orgId, conversationId }: ZipEntryFormProps): React.ReactElement {
  const [zip, setZip] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const submitQuery = async (query: Record<string, unknown>): Promise<void> => {
    if (!orgId || !onUpdateData) return;
    const result = await API.EmbedPicker.getLocations(orgId, query);
    const merged: Record<string, unknown> = {
      ...result,
      mode: "results",
      ...(data.chainTo ? { chainTo: data.chainTo } : {})
    };
    onUpdateData(merged);
  };

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (submitting || !zip.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      const query: Record<string, unknown> = {
        postalCode: zip.trim(),
        distance: data.search.radiusMiles || 25,
        sourceMessageId: message.messageId,
        sourceAction: "zip_entry_submit"
      };
      if (conversationId !== null && conversationId !== undefined) query["botConversationId"] = conversationId;
      await submitQuery(query);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Couldn't fetch locations";
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleUseMyLocation = (): void => {
    if (submitting) return;
    if (!navigator.geolocation) {
      setError("Geolocation isn't available in this browser");
      return;
    }
    setSubmitting(true);
    setError(null);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const query: Record<string, unknown> = {
            lat: pos.coords.latitude,
            lng: pos.coords.longitude,
            distance: data.search.radiusMiles || 25,
            sourceMessageId: message.messageId,
            sourceAction: "use_my_location"
          };
          if (conversationId !== null && conversationId !== undefined) query["botConversationId"] = conversationId;
          await submitQuery(query);
        } catch (err) {
          const msg = err instanceof Error ? err.message : "Couldn't fetch locations";
          setError(msg);
        } finally {
          setSubmitting(false);
        }
      },
      (err) => {
        setSubmitting(false);
        const msg = err.code === err.PERMISSION_DENIED ? "Location permission denied" : "Couldn't get your location";
        setError(msg);
      },
      { enableHighAccuracy: false, timeout: 10000, maximumAge: 60000 }
    );
  };

  const canSubmit = !submitting && !!zip.trim();

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-2 rounded-lg border border-grey-200 bg-white p-3">
      <div className="flex items-center gap-2">
        <div className="bg-secondary-50 text-secondary-400 flex h-6 w-6 shrink-0 items-center justify-center rounded-full">
          <MdLocationOn size={16} />
        </div>
        <div className="flex min-w-0 flex-1 flex-col text-sm leading-snug">
          <span className="font-semibold text-grey-900">Find Nearby Storage</span>
          <span className="text-grey-500">Enter your zip code to search</span>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <div className="relative flex h-10 min-w-0 flex-1 items-center gap-2 rounded-sm border border-primary-500 bg-linear-to-b from-grey-50 via-white via-22% to-white px-3 py-2 focus-within:border-secondary focus-within:ring-2 focus-within:ring-secondary/30">
          <input
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={10}
            placeholder="Zip code"
            value={zip}
            onChange={(e) => setZip(e.target.value)}
            disabled={submitting}
            // `text-base` (16px) on mobile stops iOS Safari auto-zooming when
            // the field is focused; `md:text-sm` restores the 14px card design
            // on desktop (where there's no focus-zoom behavior anyway).
            className="min-w-0 flex-1 bg-transparent text-base text-grey-900 placeholder:text-grey-500 focus:outline-none md:text-sm"
          />
          <button
            type="submit"
            disabled={!canSubmit}
            aria-label="Search"
            className={filterClasses(
              "flex h-6 w-6 shrink-0 items-center justify-center rounded-sm bg-primary-500 text-white transition-opacity",
              !submitting && !zip.trim() && "opacity-50"
            )}
          >
            {submitting ? <Spinner /> : <ArrowRightIcon />}
          </button>
        </div>
        <button
          type="button"
          onClick={handleUseMyLocation}
          disabled={submitting}
          className="flex shrink-0 cursor-pointer items-center gap-1 text-xs font-semibold text-primary-500 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <CurrentLocationIcon />
          Use my location
        </button>
      </div>
      {error && <div className="text-xs text-red-600">{error}</div>}
    </form>
  );
}

function ArrowRightIcon(): React.ReactElement {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M14.4 12 8.7 6.3a1 1 0 0 1 1.4-1.4l6.4 6.4a1 1 0 0 1 0 1.4l-6.4 6.4a1 1 0 0 1-1.4-1.4L14.4 12z" />
    </svg>
  );
}

/** Small indeterminate spinner (inherits `currentColor`) for loading states. */
function Spinner(): React.ReactElement {
  return (
    <svg className="animate-spin" width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="3" strokeOpacity="0.25" />
      <path d="M21 12a9 9 0 0 0-9-9" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
    </svg>
  );
}

function CurrentLocationIcon(): React.ReactElement {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="8" />
      <circle cx="12" cy="12" r="2" fill="currentColor" />
      <path d="M12 2v3M12 19v3M2 12h3M19 12h3" />
    </svg>
  );
}

interface LocationCardBodyProps {
  location: LocationOption;
  readOnly: boolean;
  onTap?: () => void;
  fetchPlacesRating?: (locationId: number) => Promise<{ rating: number; userRatingsCount: number } | null>;
  /** CTA text — "View Units" for shopping flows, "Select" for informational picks. */
  ctaLabel: string;
}

/**
 * Inner content of a location card — image+title+address, meta row, View
 * Units button. No outer border/padding so the carousel can wrap it inside
 * a single static shell while the body alone animates on index change.
 */
function LocationCardBody({ location, readOnly, onTap, fetchPlacesRating, ctaLabel }: LocationCardBodyProps): React.ReactElement {
  // The console reads these as a record rather than a live control, so the
  // secondary text steps up to grey-600 to match the rest of that panel. The
  // widget keeps grey-500, its designed value.
  const muted = readOnly ? "text-grey-600" : "text-grey-500";
  const addressLine = formatAddress(location);
  const rating = usePlacesRating(location.id, fetchPlacesRating);
  const hasUnits = typeof location.availableUnitCount === "number";
  const hasDistance = typeof location.distanceMiles === "number";
  const hasHours = !!location.openHours;

  // Track which meta items are present and stitch dividers between them so
  // the row reads like "A | B | C" without trailing/leading separators —
  // dropping a single item shouldn't leave an orphan pipe.
  const metaItems: React.ReactNode[] = [];
  if (hasUnits) metaItems.push(<span className="font-semibold text-primary-500">{location.availableUnitCount} units avail.</span>);
  if (hasDistance) metaItems.push(<span className={muted}>{location.distanceMiles!.toFixed(1)} mi</span>);
  if (rating) {
    metaItems.push(
      <span
        className={filterClasses("flex items-center gap-1", muted)}
        aria-label={`${rating.rating.toFixed(1)} stars, ${rating.userRatingsCount} reviews`}
      >
        <StarIcon />
        {rating.rating.toFixed(1)}
      </span>
    );
  }
  // `openHours` arrives from the server as a complete phrase ("Open until 6 PM",
  // "Opens Mon 9 AM", "Closed"), so it needs no label of its own. It's the longest
  // and least critical meta item, so it's the one allowed to shrink and ellipsize.
  if (hasHours) metaItems.push(<span className="min-w-0 truncate text-grey-500">{location.openHours}</span>);

  return (
    <>
      <div className="flex items-center gap-3">
        <div className="flex size-12 shrink-0 items-center justify-center overflow-hidden rounded-sm bg-grey-50 text-grey-400">
          {location.imageUrl ? <img src={location.imageUrl} alt="" className="size-full object-cover" /> : <BuildingIcon />}
        </div>
        <div className="flex min-w-0 flex-1 flex-col">
          <span className="truncate text-base font-semibold text-grey-900">{location.name}</span>
          {/* Reserve a fixed two-line height and clamp longer addresses, so
              a 1-line and a 2-line address occupy the same space — keeps every
              card (and thus the carousel container) the same height. */}
          <span className={filterClasses("line-clamp-2 min-h-10 text-sm", muted)}>{addressLine}</span>
        </div>
      </div>
      {/* Always render the meta row on a single line (fixed height, no wrap,
          clip overflow) so its presence/length never changes card height. */}
      <div className="flex min-h-5 items-center gap-x-2 overflow-hidden text-sm whitespace-nowrap">
        {metaItems.map((item, i) => (
          <React.Fragment key={i}>
            {i > 0 && <MetaDivider />}
            {item}
          </React.Fragment>
        ))}
      </div>
      {!readOnly && onTap && (
        <button
          type="button"
          onClick={onTap}
          className="flex h-10 w-full cursor-pointer items-center justify-center rounded-sm bg-primary-500 text-base font-semibold text-white hover:bg-primary-600"
        >
          {ctaLabel}
        </button>
      )}
    </>
  );
}

function MetaDivider(): React.ReactElement {
  return <span aria-hidden="true" className="h-3 w-px bg-grey-300" />;
}

function BuildingIcon(): React.ReactElement {
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M3 21h18M5 21V7l8-4v18M19 21V11l-6-4M9 9h0M9 12h0M9 15h0M9 18h0" />
    </svg>
  );
}

function StarIcon(): React.ReactElement {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="#FFB801" aria-hidden="true">
      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
    </svg>
  );
}

/**
 * Resolves a Google Places rating for a card on mount. Returns null while
 * loading, on resolver absence, or on a null result — caller renders the
 * rating row conditionally.
 */
function usePlacesRating(
  locationIdRaw: string,
  fetchPlacesRating?: (locationId: number) => Promise<{ rating: number; userRatingsCount: number } | null>
): { rating: number; userRatingsCount: number } | null {
  const [result, setResult] = useState<{ rating: number; userRatingsCount: number } | null>(null);
  useEffect(() => {
    if (!fetchPlacesRating) return;
    const locationId = Number(locationIdRaw);
    if (!Number.isFinite(locationId)) return;
    let cancelled = false;
    void fetchPlacesRating(locationId).then((r) => {
      if (!cancelled) setResult(r);
    });
    return () => {
      cancelled = true;
    };
  }, [locationIdRaw, fetchPlacesRating]);
  return result;
}

function SelectedLocationCard({ location, readOnly }: { location: LocationOption; readOnly: boolean }): React.ReactElement {
  const muted = readOnly ? "text-grey-600" : "text-grey-500";
  // Titled "Selected Store" card — Figma Chat-Invisibles node 72:8182.
  return (
    <div className="flex flex-col overflow-hidden rounded-lg border border-grey-200 bg-white">
      <div className="border-b border-grey-200 px-3 py-2">
        <span className={filterClasses("text-sm font-semibold", muted)}>Selected Store</span>
      </div>
      <div className="px-3 pt-3">
        <div className="flex items-center gap-2 border-b border-grey-200 pb-3">
          <div className="flex size-12 shrink-0 items-center justify-center overflow-hidden rounded bg-grey-50 text-grey-400">
            {location.imageUrl ? <img src={location.imageUrl} alt="" className="size-full object-cover" /> : <BuildingIcon />}
          </div>
          <div className="flex min-w-0 flex-1 flex-col">
            <span className="text-base font-semibold text-grey-900">{location.name}</span>
            <span className={filterClasses("text-sm", muted)}>{formatAddress(location)}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function DismissedNote({ time, readOnly, label: labelText }: { time?: string; readOnly: boolean; label: string }): React.ReactElement {
  // Figma Chat-Invisibles node 90:1660. In the console this is one more
  // internal row, so it takes the shared pill's 4px radius, grey-600 text and
  // mono token; the widget keeps the original treatment.
  const label = time ? `${labelText} • ${time}` : labelText;
  return (
    <div className="flex justify-center">
      <div
        className={filterClasses(
          "inline-flex items-center gap-2 bg-grey-50 px-2 py-1",
          readOnly ? "rounded-sm text-grey-600" : "rounded text-grey-500"
        )}
      >
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" className="shrink-0" aria-hidden="true">
          <circle cx="8" cy="8" r="6.5" />
          <line x1="8" y1="7" x2="8" y2="11" strokeLinecap="round" />
          <circle cx="8" cy="5" r="0.75" fill="currentColor" stroke="none" />
        </svg>
        <span className={readOnly ? "body-mono" : "font-mono text-xs leading-4"}>{label}</span>
      </div>
    </div>
  );
}

function formatAddress(location: LocationOption): string {
  const parts = [location.street, location.city, location.state].filter(Boolean);
  return parts.join(", ");
}
